import { ReactiveElement } from "@nrk/reactive-element";
import { durationToReadableString } from "../utils/duration.js";
import { getElementOfType } from "../utils/dom.js";
import { parseSrcset, matchSrcset } from "../utils/srcset.js";
class PosterElement extends ReactiveElement {
  static get reactiveAttributes() {
    return {
      heading: String,
      simple: Boolean,
      src: String,
      srcset: String,
      subheading: String,
      hidden: Boolean
    };
  }
  static get reactiveProperties() {
    return {
      availability: Object,
      dimensions: (element, _dimensions) => {
        if (element.srcsetConditions) {
          element.src = element.matchSrcset(element.srcsetConditions);
        }
      },
      duration: Number,
      isLive: Boolean,
      isTimeRunning: (element, isTimeRunning) => {
        if (isTimeRunning) {
          element.hidden = true;
        }
      },
      legalAgeText: String,
      metadata: (element, metadata) => {
        if (metadata) {
          element.copyMetadata(metadata);
        }
      },
      muted: Boolean,
      startTime: Number,
      srcset: String,
      videoSrc: (element, _videoSrc) => {
        if (!element.player.isBinging) {
          element.hidden = false;
        }
      }
    };
  }
  /** Availability data for the src */
  availability;
  /** Player dimensions (shadowed Player property) */
  dimensions;
  /** The media duration in seconds */
  duration = 0;
  /** The heading text */
  heading;
  /** Flag indicating whether the media is live or on-demand */
  isLive;
  /** Flag indicating that media playback is ongoing (shadowed Player property) */
  isTimeRunning;
  /** The legal age designation */
  legalAgeText;
  /** The legal age to display (9+, 16+ etc) */
  legalDisplayAge;
  /** Metadata for the current Player source (shadowed Player property) */
  metadata;
  /** Flag indicating that audio is muted (shadowed Player property) */
  muted;
  /** Attribute flag indicating simple layout mode */
  simple = false;
  /** The initial playhead position in seconds */
  startTime;
  /** The image source reference */
  src;
  /** The image srcset reference */
  srcset;
  /** The subheading text */
  subheading;
  /** The player source (src) (shadowed Player property) */
  videoSrc;
  /** The PlayerElement parent instance */
  player;
  manualOverrides = {
    srcset: false,
    heading: false,
    subheading: false
  };
  srcsetConditions;
  _unshadowProperties;
  readyCallback() {
    this.player = getElementOfType(this);
    this._unshadowProperties = this.player.shadowProperties(
      ["dimensions", "isTimeRunning", "metadata", "muted", "src:videoSrc"],
      this
    );
    if (!this.isBootstrapped) {
      this.manualOverrides = {
        srcset: this.srcset !== void 0,
        heading: this.heading !== void 0,
        subheading: this.subheading !== void 0
      };
    }
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "srcset" && this.srcset) {
      this.srcsetConditions = parseSrcset(this.srcset);
      this.src = this.matchSrcset(this.srcsetConditions);
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (!this.isConnected) {
      return;
    }
    if (this.src && changedProperties.has("src")) {
      this.player.poster = this.src;
    }
  }
  disconnectedCallback() {
    this._unshadowProperties?.();
    this.player = null;
    super.disconnectedCallback();
  }
  copyMetadata(metadata) {
    const legalAgeBody = metadata.legalAge?.body;
    this.duration = metadata.duration;
    this.isLive = metadata.isLive;
    this.availability = metadata.availability;
    this.startTime = metadata.startTime;
    if (legalAgeBody && "rating" in legalAgeBody) {
      this.legalAgeText = legalAgeBody.rating.displayValue;
      this.legalDisplayAge = legalAgeBody.rating.displayAge;
    }
    if (!this.manualOverrides.srcset && metadata.posterSrcset) {
      this.srcset = metadata.posterSrcset;
    }
    if (!this.manualOverrides.subheading && metadata.subheading) {
      this.subheading = metadata.subheading;
    }
    if (!this.manualOverrides.heading && metadata.heading) {
      this.heading = metadata.heading;
    }
  }
  /**
   * Retrieve a descriptive text string derived from state
   */
  get accessibleDescription() {
    const lines = [];
    const readableDuration = !this.isLive && durationToReadableString(this.duration);
    if (this.isLive) {
      lines.push("Direkte n\xE5");
    }
    if (this.heading) {
      lines.push(`Spill av ${this.heading}`);
    }
    if (this.legalAgeText) {
      lines.push(this.legalAgeText);
    }
    if (readableDuration) {
      lines.push(`Varigheten er ${readableDuration}`);
    }
    return lines.length ? `${lines.join(". ")}.` : "";
  }
  matchSrcset(srcsetConditions) {
    const { width } = this.getBoundingClientRect();
    return matchSrcset({
      srcsetConditions,
      devicePixelRatio: window.devicePixelRatio,
      elementWidth: width
    }) ?? this.src;
  }
}
export {
  PosterElement
};
